import React from 'react';
import { motion } from 'framer-motion';
import {
  Building2,
  Ticket,
  Trophy,
  Users,
  TrendingUp,
  TrendingDown,
  ArrowRight,
  DollarSign,
  Clock,
  AlertCircle,
} from 'lucide-react';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { Card, Badge, cn, formatNaira, formatNumber, staggerChildren, fadeInUp } from '@raffle/ui';
import { Link } from 'react-router-dom';

// Mock data
const revenueData = [
  { month: 'Jan', revenue: 12500000, tickets: 2500 },
  { month: 'Feb', revenue: 18200000, tickets: 3640 },
  { month: 'Mar', revenue: 22100000, tickets: 4420 },
  { month: 'Apr', revenue: 19800000, tickets: 3960 },
  { month: 'May', revenue: 28500000, tickets: 5700 },
  { month: 'Jun', revenue: 32100000, tickets: 6420 },
];

const propertyStatusData = [
  { name: 'Active', value: 8, color: '#22C55E' },
  { name: 'Pending', value: 3, color: '#D4AF37' },
  { name: 'Completed', value: 12, color: '#3B82F6' },
  { name: 'Cancelled', value: 1, color: '#EF4444' },
];

const recentTickets = [
  { id: 't1', property: 'Luxury Villa in Lekki', buyer: 'John Doe', amount: 50000, time: '2 min ago' },
  { id: 't2', property: 'Modern Penthouse VI', buyer: 'Jane Smith', amount: 40000, time: '5 min ago' },
  { id: 't3', property: 'Beachfront Apartment', buyer: 'Mike Johnson', amount: 25000, time: '12 min ago' },
  { id: 't4', property: 'Executive Mansion', buyer: 'Sarah Williams', amount: 100000, time: '18 min ago' },
  { id: 't5', property: 'Garden Terrace', buyer: 'Chris Brown', amount: 10000, time: '25 min ago' },
];

const upcomingDraws = [
  { id: 'd1', property: 'Garden Terrace in Ikeja', date: '2026-02-10', ticketsSold: 11456, total: 12000 },
  { id: 'd2', property: 'Serviced Apartment in Ikoyi', date: '2026-02-15', ticketsSold: 8932, total: 10000 },
  { id: 'd3', property: 'Beachfront Apartment', date: '2026-02-20', ticketsSold: 6789, total: 8000 },
];

export const DashboardPage: React.FC = () => {
  const stats = [
    {
      label: 'Total Revenue',
      value: formatNaira(133200000),
      change: '+12.5%',
      isPositive: true,
      icon: DollarSign,
      color: 'text-gold-500',
      bgColor: 'bg-gold-500/10',
    },
    {
      label: 'Active Properties',
      value: '8',
      change: '+2',
      isPositive: true,
      icon: Building2,
      color: 'text-blue-400',
      bgColor: 'bg-blue-500/10',
    },
    {
      label: 'Tickets Sold',
      value: formatNumber(26640),
      change: '+18.2%',
      isPositive: true,
      icon: Ticket,
      color: 'text-green-400',
      bgColor: 'bg-green-500/10',
    },
    {
      label: 'Total Users',
      value: formatNumber(4521),
      change: '+8.1%',
      isPositive: true,
      icon: Users,
      color: 'text-purple-400',
      bgColor: 'bg-purple-500/10',
    },
  ];

  return (
    <div className="space-y-8">
      {/* Stats Grid */}
      <motion.div
        className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4"
        variants={staggerChildren}
        initial="hidden"
        animate="show"
      >
        {stats.map((stat, index) => (
          <motion.div key={index} variants={fadeInUp}>
            <Card className="p-6 stat-card-gradient">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-text-muted">{stat.label}</p>
                  <p className="font-mono text-2xl font-bold text-text-primary mt-1 tabular-nums">
                    {stat.value}
                  </p>
                  <div className="flex items-center gap-1 mt-2">
                    {stat.isPositive ? (
                      <TrendingUp className="w-4 h-4 text-vault-success" />
                    ) : (
                      <TrendingDown className="w-4 h-4 text-vault-error" />
                    )}
                    <span className={cn(
                      'text-sm font-medium',
                      stat.isPositive ? 'text-vault-success' : 'text-vault-error'
                    )}>
                      {stat.change}
                    </span>
                    <span className="text-xs text-text-muted">vs last month</span>
                  </div>
                </div>
                <div className={cn('p-3 rounded-vault', stat.bgColor)}>
                  <stat.icon className={cn('w-6 h-6', stat.color)} />
                </div>
              </div>
            </Card>
          </motion.div>
        ))}
      </motion.div>

      {/* Charts Row */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Revenue Chart */}
        <Card className="lg:col-span-2 p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-display text-lg font-semibold text-text-primary">
              Revenue Overview
            </h3>
            <select className="text-sm bg-surface-2 border border-white/10 rounded-vault px-3 py-1.5 text-text-secondary">
              <option>Last 6 months</option>
              <option>Last year</option>
              <option>All time</option>
            </select>
          </div>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={revenueData}>
                <defs>
                  <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#D4AF37" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#D4AF37" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#2A2A2A" />
                <XAxis dataKey="month" stroke="#71717A" fontSize={12} />
                <YAxis stroke="#71717A" fontSize={12} tickFormatter={(v) => `₦${v / 1000000}M`} />
                <Tooltip
                  contentStyle={{
                    background: '#1E1E1E',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: '8px',
                  }}
                  formatter={(value: number) => [formatNaira(value), 'Revenue']}
                />
                <Area
                  type="monotone"
                  dataKey="revenue"
                  stroke="#D4AF37"
                  strokeWidth={2}
                  fill="url(#revenueGradient)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* Property Status Pie Chart */}
        <Card className="p-6">
          <h3 className="font-display text-lg font-semibold text-text-primary mb-6">
            Property Status
          </h3>
          <div className="h-[200px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={propertyStatusData}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={70}
                  paddingAngle={4}
                  dataKey="value"
                >
                  {propertyStatusData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    background: '#1E1E1E',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: '8px',
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="grid grid-cols-2 gap-2 mt-4">
            {propertyStatusData.map((item) => (
              <div key={item.name} className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                <span className="text-sm text-text-secondary">{item.name}</span>
                <span className="text-sm font-mono text-text-primary ml-auto">{item.value}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Tables Row */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Recent Tickets */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-display text-lg font-semibold text-text-primary">
              Recent Ticket Sales
            </h3>
            <Link to="/tickets" className="text-sm text-gold-500 hover:text-gold-400 flex items-center gap-1">
              View all <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="space-y-4">
            {recentTickets.map((ticket) => (
              <div key={ticket.id} className="flex items-center justify-between py-3 border-b border-white/5 last:border-0">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-text-primary truncate">{ticket.property}</p>
                  <p className="text-xs text-text-muted">{ticket.buyer} • {ticket.time}</p>
                </div>
                <span className="font-mono text-sm font-semibold text-gold-500 tabular-nums">
                  +{formatNaira(ticket.amount)}
                </span>
              </div>
            ))}
          </div>
        </Card>

        {/* Upcoming Draws */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-display text-lg font-semibold text-text-primary">
              Upcoming Draws
            </h3>
            <Link to="/draws" className="text-sm text-gold-500 hover:text-gold-400 flex items-center gap-1">
              View all <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="space-y-4">
            {upcomingDraws.map((draw) => {
              const progress = (draw.ticketsSold / draw.total) * 100;
              return (
                <div key={draw.id} className="p-4 rounded-vault bg-surface-2/50 border border-white/5">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <p className="text-sm font-medium text-text-primary">{draw.property}</p>
                      <p className="text-xs text-text-muted flex items-center gap-1 mt-1">
                        <Clock className="w-3 h-3" />
                        {new Date(draw.date).toLocaleDateString('en-GB', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric',
                        })}
                      </p>
                    </div>
                    <Badge variant={progress >= 90 ? 'success' : progress >= 70 ? 'warning' : 'default'}>
                      {progress.toFixed(0)}%
                    </Badge>
                  </div>
                  <div className="h-2 rounded-full bg-surface-3 overflow-hidden">
                    <div
                      className="h-full bg-gold-500 rounded-full transition-all"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                  <p className="text-xs text-text-muted mt-2">
                    {formatNumber(draw.ticketsSold)} / {formatNumber(draw.total)} tickets sold
                  </p>
                </div>
              );
            })}
          </div>
        </Card>
      </div>

      {/* Alerts */}
      <Card className="p-6 border-gold-500/20">
        <div className="flex items-start gap-4">
          <div className="p-2 rounded-vault bg-gold-500/10">
            <AlertCircle className="w-5 h-5 text-gold-500" />
          </div>
          <div className="flex-1">
            <h4 className="font-medium text-text-primary">Action Required</h4>
            <p className="text-sm text-text-secondary mt-1">
              3 support tickets are waiting for your response. 2 draws are scheduled for next week.
            </p>
          </div>
          <Link to="/support">
            <Badge variant="warning" className="cursor-pointer hover:bg-gold-500/20">
              View Tickets
            </Badge>
          </Link>
        </div>
      </Card>
    </div>
  );
};
